import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { withdrawalAPI } from '../../services/api';
import Sidebar from '../../components/Common/Sidebar';
import {
  Banknote, Plus, University, CheckCircle2, Clock,
  XCircle, Ban, Loader2, X, AlertTriangle,
} from 'lucide-react';
import { toast } from 'react-toastify';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

/* ── status badge ── */
const STATUS_MAP = {
  pending:   { bg: C.warnSoft,    color: '#92400E', Icon: Clock         },
  approved:  { bg: C.primarySoft, color: C.primary, Icon: CheckCircle2  },
  processed: { bg: C.successSoft, color: '#065F46', Icon: CheckCircle2  },
  rejected:  { bg: C.dangerSoft,  color: '#991B1B', Icon: XCircle       },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status?.toLowerCase()] || STATUS_MAP.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>
      <s.Icon size={10} strokeWidth={2.5} />
      {(status || 'pending').charAt(0).toUpperCase() + (status || 'pending').slice(1)}
    </span>
  );
};

/* ── stat card ── */
const StatCard = ({ icon: Icon, iconColor, iconBg, label, value }) => (
  <div style={{
    background: C.surface, border: `1.5px solid ${C.border}`,
    borderRadius: C.radius, padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 1px 3px rgba(20,20,43,.04)',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 6px' }}>
          {label}
        </p>
        <p style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-0.01em', lineHeight: 1, minHeight: 26, display: 'flex', alignItems: 'center' }}>
          {value ?? '——'}
        </p>
      </div>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={17} color={iconColor} strokeWidth={1.8} />
      </div>
    </div>
  </div>
);

const inputStyle = (focused) => ({
  width: '100%', padding: '9px 12px', fontSize: 13, color: C.ink,
  background: C.bg, border: `1.5px solid ${focused ? C.primary : C.border}`,
  borderRadius: C.radiusSm, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color .15s',
});

const TH = ['Request ID', 'Amount', 'Bank Details', 'Status', 'Date', 'Action'];

const EMPTY_FORM = {
  amount: '', bank_account_number: '', bank_ifsc: '', bank_name: '', account_holder_name: '',
};

const UserWithdrawal = () => {
  const { user } = useAuth();
  const [loading,     setLoading]     = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [statistics,  setStatistics]  = useState(null);
  const [showModal,   setShowModal]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [focused,     setFocused]     = useState(null);

  useEffect(() => {
    fetchWithdrawals();
    fetchStatistics();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const res = await withdrawalAPI.getList();
      setWithdrawals(res.data.data.withdrawals);
    } catch {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const res = await withdrawalAPI.getStatistics();
      setStatistics(res.data.data);
    } catch { /* silent */ }
  };

  const handleInput = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(formData.amount);
    if (amt < 500)               { toast.error('Minimum withdrawal is ₹500');   return; }
    if (amt > user.wallet_balance){ toast.error('Insufficient wallet balance');  return; }
    setSubmitting(true);
    try {
      await withdrawalAPI.request(formData);
      toast.success('Withdrawal request submitted!');
      setShowModal(false);
      setFormData(EMPTY_FORM);
      await fetchWithdrawals();
      await fetchStatistics();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this withdrawal request?')) return;
    try {
      await withdrawalAPI.cancel(requestId);
      toast.success('Request cancelled');
      await fetchWithdrawals();
      await fetchStatistics();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not cancel request');
    }
  };

  const hasPending = (statistics?.pending_count || 0) > 0;

  return (
    <div className="dashboard-layout">
      <Sidebar role="user" />

      <div className="dashboard-content" style={{ background: C.bg, minHeight: '100vh', padding: '28px 28px 40px' }}>

        {/* ── page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 26, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Withdrawal Requests</h2>
            <p  style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Manage your withdrawal requests</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            disabled={hasPending}
            title={hasPending ? 'You have a pending request. Wait for approval.' : ''}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: C.radiusSm,
              background: hasPending ? C.muted : C.primary, color: '#fff',
              fontWeight: 700, fontSize: 13, border: 'none',
              cursor: hasPending ? 'not-allowed' : 'pointer',
            }}
          >
            <Plus size={14} strokeWidth={2.5} /> New Request
          </button>
        </div>

        {/* ── stat cards ── */}
        {statistics && (
          <div className="withdrawal-stat-grid">
            <StatCard icon={Banknote}     iconColor={C.primary}  iconBg={C.primarySoft}
              label="Total Requested" value={fmtINR(statistics.total_requested)} />
            <StatCard icon={CheckCircle2} iconColor="#065F46"    iconBg={C.successSoft}
              label="Total Approved"  value={fmtINR(statistics.total_approved)}  />
            <StatCard icon={Clock}        iconColor="#92400E"    iconBg={C.warnSoft}
              label="Pending Amount"  value={fmtINR(statistics.total_pending)}   />
            <StatCard icon={XCircle}      iconColor="#991B1B"    iconBg={C.dangerSoft}
              label="Total Rejected"  value={fmtINR(statistics.total_rejected)}  />
          </div>
        )}

        {/* ── pending alert ── */}
        {hasPending && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 16px', marginBottom: 18,
            background: C.warnSoft, border: `1px solid #FDE68A`,
            borderRadius: C.radiusSm, fontSize: 13, color: '#92400E',
          }}>
            <Clock size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            You have <strong style={{ margin: '0 4px' }}>{statistics.pending_count}</strong> pending withdrawal request(s). Wait for admin approval before creating a new one.
          </div>
        )}

        {/* ── table card ── */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(20,20,43,.04)',
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 16 }}>
            Withdrawal History
          </p>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
              <Loader2 size={30} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.borderSoft}` }}>
                    {TH.map(h => (
                      <th key={h} style={{
                        padding: '9px 14px', textAlign: 'left',
                        fontSize: 11, fontWeight: 700, color: C.muted,
                        textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.length > 0 ? withdrawals.map(w => (
                    <tr key={w.id}
                      style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = C.bg}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: C.body, whiteSpace: 'nowrap' }}>
                        {w.request_id}
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 800, color: C.ink, whiteSpace: 'nowrap' }}>
                        {fmtINR(w.amount)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{w.bank_name}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>A/c: {w.bank_account_number}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>IFSC: {w.bank_ifsc}</div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <StatusBadge status={w.status} />
                        {w.admin_remarks && (
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{w.admin_remarks}</div>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', color: C.muted, fontSize: 12, whiteSpace: 'nowrap' }}>
                        {fmtDate(w.created_at)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {w.status === 'pending' && (
                          <button
                            onClick={() => handleCancel(w.request_id)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '5px 12px', borderRadius: C.radiusSm,
                              background: C.dangerSoft, color: '#991B1B',
                              border: `1px solid #FECACA`, fontWeight: 600, fontSize: 12, cursor: 'pointer',
                            }}
                          >
                            <Ban size={12} strokeWidth={2} /> Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={TH.length} style={{ padding: '60px 0', textAlign: 'center', color: C.muted, fontSize: 14 }}>
                        No withdrawal requests yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Request Withdrawal Modal ── */}
      {showModal && (
        <div
          onClick={() => !submitting && setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: C.radius,
              width: '100%', maxWidth: 480, padding: '28px 28px 24px',
              boxShadow: '0 20px 60px rgba(20,20,43,.18)',
              maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: C.primarySoft, color: C.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Banknote size={17} strokeWidth={1.8} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>Request Withdrawal</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Min ₹500 · Balance: {fmtINR(user?.wallet_balance)}</p>
                </div>
              </div>
              <button onClick={() => !submitting && setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* balance info */}
              <div style={{
                background: C.primarySoft, border: `1px solid ${C.primaryTint}`,
                borderRadius: C.radiusSm, padding: '10px 14px', marginBottom: 18,
                fontSize: 12, color: C.body,
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span><strong style={{ color: C.primary }}>Available Balance</strong></span>
                <span style={{ fontWeight: 700, color: C.ink }}>{fmtINR(user?.wallet_balance)}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Amount */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>
                    Withdrawal Amount *
                  </label>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    border: `1.5px solid ${focused === 'amount' ? C.primary : C.border}`,
                    borderRadius: C.radiusSm, background: C.bg, overflow: 'hidden',
                  }}>
                    <span style={{ padding: '0 12px', fontSize: 16, fontWeight: 700, color: C.muted }}>₹</span>
                    <input
                      type="number" name="amount" value={formData.amount} onChange={handleInput}
                      onFocus={() => setFocused('amount')} onBlur={() => setFocused(null)}
                      placeholder="0.00" min="500" max={user?.wallet_balance} required
                      style={{ flex: 1, padding: '10px 12px 10px 0', fontSize: 14, fontWeight: 700, color: C.ink, background: 'transparent', border: 'none', outline: 'none' }}
                    />
                  </div>
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Minimum: ₹500</p>
                </div>

                {/* Bank Name */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Bank Name *</label>
                  <input type="text" name="bank_name" value={formData.bank_name} onChange={handleInput}
                    onFocus={() => setFocused('bank_name')} onBlur={() => setFocused(null)}
                    placeholder="e.g. State Bank of India" required
                    style={inputStyle(focused === 'bank_name')} />
                </div>

                {/* Account Holder */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Account Holder Name *</label>
                  <input type="text" name="account_holder_name" value={formData.account_holder_name} onChange={handleInput}
                    onFocus={() => setFocused('acname')} onBlur={() => setFocused(null)}
                    placeholder="As per bank records" required
                    style={inputStyle(focused === 'acname')} />
                </div>

                {/* Account Number + IFSC */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>Account Number *</label>
                    <input type="text" name="bank_account_number" value={formData.bank_account_number} onChange={handleInput}
                      onFocus={() => setFocused('acno')} onBlur={() => setFocused(null)}
                      placeholder="Account number" required
                      style={inputStyle(focused === 'acno')} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' }}>IFSC Code *</label>
                    <input type="text" name="bank_ifsc" value={formData.bank_ifsc} onChange={handleInput}
                      onFocus={() => setFocused('ifsc')} onBlur={() => setFocused(null)}
                      placeholder="ABCD0123456" maxLength="11" required
                      style={{ ...inputStyle(focused === 'ifsc'), textTransform: 'uppercase' }} />
                  </div>
                </div>
              </div>

              {/* warning note */}
              <div style={{
                background: C.warnSoft, border: `1px solid #FDE68A`,
                borderRadius: C.radiusSm, padding: '10px 14px', margin: '18px 0',
                fontSize: 12, color: '#92400E',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 6 }}>
                  <AlertTriangle size={13} strokeWidth={2} /> Important
                </div>
                <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.8 }}>
                  <li>Processing time: 24–48 hours</li>
                  <li>Ensure bank details are correct</li>
                  <li>Amount deducted from wallet immediately</li>
                  <li>Pending requests can be cancelled</li>
                </ul>
              </div>

              {/* actions */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" disabled={submitting} onClick={() => setShowModal(false)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                    background: C.bg, color: C.body, border: `1px solid ${C.border}`,
                    fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                    background: submitting ? C.muted : C.primary,
                    color: '#fff', border: 'none',
                    fontWeight: 700, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  }}>
                  {submitting
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</>
                    : <><CheckCircle2 size={14} strokeWidth={2} /> Submit Request</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .withdrawal-stat-grid {
          display: grid;
          grid-template-columns: repeat(4,1fr);
          gap: 18px;
          margin-bottom: 22px;
        }
        @media (max-width: 700px) {
          .withdrawal-stat-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 400px) {
          .withdrawal-stat-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default UserWithdrawal;
